import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  supabase,
  type EvaluationField,
  type EvaluationForm,
  type EvaluationSubmission,
} from '@/lib/supabase'
import { useAuth } from '@/auth/useAuth'
import {
  Badge,
  Button,
  Card,
  DataBoundaryBar,
  EmptyState,
  ErrorNote,
  PageHeader,
} from '@/components/ui'

export function EvaluationPage() {
  const { profile } = useAuth()
  const queryClient = useQueryClient()
  const [openForm, setOpenForm] = useState<EvaluationForm | null>(null)

  const { data: forms } = useQuery({
    queryKey: ['evaluation-forms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('evaluation_forms')
        .select('id, code, title, description, audience, schema, version')
        .eq('is_active', true)
        .order('title')
      if (error) throw error
      return data as EvaluationForm[]
    },
  })

  // No org filter in this query, on purpose. The policy adds
  // "org_id = current_org_id()" server side, so a bug here cannot widen it,
  // and a filter here would imply the client is what keeps partners apart.
  const { data: submissions, isLoading } = useQuery({
    queryKey: ['evaluation-submissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('evaluation_submissions')
        .select('id, form_id, org_id, cohort_ref, participant_ref, answers, status, consent_given, submitted_at, created_at')
        .order('created_at', { ascending: false })
        .limit(100)
      if (error) throw error
      return data as EvaluationSubmission[]
    },
  })

  const agreementSigned = Boolean(profile?.organisations?.data_agreement_signed_at)

  if (openForm) {
    return (
      <SubmissionForm
        form={openForm}
        onDone={() => {
          setOpenForm(null)
          void queryClient.invalidateQueries({ queryKey: ['evaluation-submissions'] })
        }}
      />
    )
  }

  return (
    <>
      <PageHeader title="Evaluation">
        Enter your cohort data here and it goes straight into the shared measurement set. You keep
        your own records, and can export them at any time.
      </PageHeader>

      <DataBoundaryBar />

      {!agreementSigned && (
        <div className="mb-5">
          <ErrorNote>
            Your partner data agreement is not recorded yet. Talk to the Ki Tua o Matariki team
            before entering participant data, so the terms are settled first.
          </ErrorNote>
        </div>
      )}

      <section className="mb-8">
        <h2 className="mb-3 text-base text-ink">Start a new entry</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {(forms ?? []).map((form) => (
            <Card key={form.id}>
              <h3 className="text-sm font-semibold text-ink">{form.title}</h3>
              {form.description && <p className="mt-1 text-sm text-inksoft">{form.description}</p>}
              <Button className="mt-3" variant="secondary" onClick={() => setOpenForm(form)}>
                Open form
              </Button>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-base text-ink">Your entries</h2>
        {isLoading && <p className="text-sm text-inksoft">Loading…</p>}
        {!isLoading && (submissions ?? []).length === 0 && (
          <EmptyState
            title="No entries yet"
            body="Once you run a session, record it here. Entries build your own reporting as well as the wider impact picture."
          />
        )}
        {(submissions ?? []).length > 0 && (
          <div className="overflow-x-auto rounded-md border border-line bg-surface">
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-inksoft">
                <tr>
                  <th className="px-4 py-2 font-medium">Cohort</th>
                  <th className="px-4 py-2 font-medium">Participant ref</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Recorded</th>
                </tr>
              </thead>
              <tbody>
                {(submissions ?? []).map((s) => (
                  <tr key={s.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-2">{s.cohort_ref ?? '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs">{s.participant_ref ?? '—'}</td>
                    <td className="px-4 py-2">
                      <Badge tone={s.status === 'submitted' ? 'good' : 'neutral'}>
                        {s.status === 'submitted' ? 'Submitted' : 'Draft'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-inksoft">
                      {new Date(s.created_at).toLocaleDateString('en-NZ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

function SubmissionForm({ form, onDone }: { form: EvaluationForm; onDone: () => void }) {
  const [answers, setAnswers] = useState<Record<string, unknown>>({})
  const [cohortRef, setCohortRef] = useState('')
  const [participantRef, setParticipantRef] = useState('')
  const [consent, setConsent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async (status: 'draft' | 'submitted') => {
      // org_id is not sent. A database trigger stamps it from the session, so
      // a client cannot file a record against another organisation even by
      // editing the request.
      const { error } = await supabase.from('evaluation_submissions').insert({
        form_id: form.id,
        cohort_ref: cohortRef || null,
        participant_ref: participantRef || null,
        answers,
        status,
        consent_given: consent,
      })
      if (error) throw error
    },
    onSuccess: onDone,
    onError: () => setError('That entry could not be saved. Check the required fields and try again.'),
  })

  function submit(status: 'draft' | 'submitted') {
    setError(null)
    if (status === 'submitted' && !consent) {
      setError('Record consent before submitting. Save it as a draft if you are still waiting.')
      return
    }
    const missing = form.schema.filter((f) => f.required && answers[f.key] === undefined)
    if (status === 'submitted' && missing.length > 0) {
      setError(`Still to fill in: ${missing.map((f) => f.label).join(', ')}.`)
      return
    }
    save.mutate(status)
  }

  return (
    <>
      <PageHeader title={form.title}>
        {form.description ?? undefined}
      </PageHeader>

      <DataBoundaryBar />

      <Card className="max-w-2xl">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-ink">Cohort</span>
            <input
              value={cohortRef}
              onChange={(e) => setCohortRef(e.target.value)}
              placeholder="e.g. Term 3 evening group"
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Participant reference</span>
            <input
              value={participantRef}
              onChange={(e) => setParticipantRef(e.target.value)}
              placeholder="Your own code, not a name"
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            />
            <span className="mt-1 block text-xs text-inksoft">
              Use a code your team can trace back. Names stay with you.
            </span>
          </label>
        </div>

        <div className="mt-6 space-y-5 border-t border-line pt-5">
          {form.schema.map((field) => (
            <FieldInput
              key={field.key}
              field={field}
              value={answers[field.key]}
              onChange={(value) => setAnswers((prev) => ({ ...prev, [field.key]: value }))}
            />
          ))}
        </div>

        <label className="mt-6 flex items-start gap-2 border-t border-line pt-5 text-sm">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1"
          />
          <span>
            The participant has given consent for this information to be recorded and used as set
            out in the participant information sheet.
            <span className="ref ml-2">Privacy Act 2020</span>
          </span>
        </label>

        {error && <div className="mt-4"><ErrorNote>{error}</ErrorNote></div>}

        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={() => submit('submitted')} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Submit entry'}
          </Button>
          <Button variant="secondary" onClick={() => submit('draft')} disabled={save.isPending}>
            Save as draft
          </Button>
          <Button variant="quiet" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </Card>
    </>
  )
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: EvaluationField
  value: unknown
  onChange: (value: unknown) => void
}) {
  const label = (
    <span className="font-medium text-ink">
      {field.label}
      {field.required && <span className="ml-1 text-alert">*</span>}
    </span>
  )

  if (field.type === 'scale') {
    const min = field.min ?? 1
    const max = field.max ?? 5
    const options = Array.from({ length: max - min + 1 }, (_, i) => min + i)
    return (
      <fieldset className="text-sm">
        <legend>{label}</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {options.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              aria-pressed={value === n}
              className={`h-10 w-10 rounded-sm border text-sm ${
                value === n ? 'border-pounamu bg-pounamu text-white' : 'border-line text-inksoft'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </fieldset>
    )
  }

  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
    )
  }

  if (field.type === 'number') {
    return (
      <label className="block text-sm">
        {label}
        <input
          type="number"
          value={value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          className="mt-1 w-32 rounded-sm border border-line px-3 py-2"
        />
      </label>
    )
  }

  return (
    <label className="block text-sm">
      {label}
      <textarea
        rows={3}
        value={value === undefined ? '' : String(value)}
        onChange={(e) => onChange(e.target.value || undefined)}
        className="mt-1 w-full rounded-sm border border-line px-3 py-2"
      />
    </label>
  )
}
